import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";

const navItems = [
  { label: "首页", to: "/", match: (path: string) => path === "/" },
  { label: "资讯", to: "/news", match: (path: string) => path.startsWith("/news") },
  { label: "作品", to: "/works", match: (path: string) => path.startsWith("/works") },
  { label: "角色专题", to: "/characters", match: (path: string) => path.startsWith("/characters") },
  { label: "关于", to: "/about", match: (path: string) => path.startsWith("/about") },
];

const typeLabels: Record<string, string> = {
  news: "资讯",
  works: "作品",
  characters: "角色",
};

const typeLink = (type: string, id: string) => {
  if (type === "news") return `/news/detail/${id}`;
  if (type === "works") return `/works/detail/${id}`;
  return `/characters/detail/${id}`;
};

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [favOpen, setFavOpen] = useState(false);
  const favRef = useRef<HTMLDivElement>(null);
  const { favorites, toggleFavorite } = useFavorites();

  useEffect(() => {
    setMobileOpen(false);
    setFavOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (favRef.current && !favRef.current.contains(event.target as Node)) {
        setFavOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search");
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between gap-4 h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer" aria-label="age动漫首页">
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center font-heading font-extrabold text-lg md:text-xl">
              a
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-extrabold text-lg md:text-xl text-foreground-950 tracking-tight">
                age<span className="text-primary-500">动漫</span>
              </span>
              <span className="hidden sm:block text-[10px] tracking-[0.22em] text-foreground-400 mt-1">
                ANIME PORTAL
              </span>
            </span>
          </Link>

          <nav aria-label="主导航" className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const active = item.match(location.pathname);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`relative px-3.5 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                    active
                      ? "text-primary-600 font-medium bg-primary-50"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <form onSubmit={submitSearch} className="hidden md:flex items-center" role="search">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索资讯、作品、角色"
                  aria-label="站内搜索"
                  className="w-40 lg:w-56 h-9 rounded-full border border-background-300 bg-background-100 pl-8 pr-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                />
              </div>
              <button
                type="submit"
                className="ml-2 h-9 px-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>

            <div className="relative" ref={favRef}>
              <button
                type="button"
                onClick={() => setFavOpen((prev) => !prev)}
                aria-label="我的收藏"
                className="relative w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-heart-line text-base"></i>
                {favorites.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary-500 text-background-50 text-[10px] font-medium flex items-center justify-center">
                    {favorites.length}
                  </span>
                )}
              </button>
              {favOpen && (
                <div className="absolute right-0 mt-2 w-72 md:w-80 rounded-lg border border-background-200 bg-background-50 p-3 z-50 animate-fade">
                  <div className="flex items-center justify-between mb-2 px-1">
                    <span className="text-sm font-semibold text-foreground-900">我的收藏</span>
                    <span className="text-xs text-foreground-400">{favorites.length} 条</span>
                  </div>
                  {favorites.length === 0 ? (
                    <p className="text-xs text-foreground-500 px-1 py-4 leading-relaxed">
                      还没有收藏内容，去资讯或作品详情页点击收藏按钮试试。
                    </p>
                  ) : (
                    <ul className="max-h-72 overflow-y-auto flex flex-col gap-1">
                      {favorites.slice(0, 8).map((item) => (
                        <li key={`${item.type}-${item.id}`} className="flex items-center gap-2 rounded-md hover:bg-background-100 px-1.5 py-1.5">
                          <Link
                            to={typeLink(item.type, item.id)}
                            className="flex-1 min-w-0 flex items-center gap-2 cursor-pointer"
                          >
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-900 shrink-0">
                              {typeLabels[item.type]}
                            </span>
                            <span className="text-xs text-foreground-800 truncate">{item.title}</span>
                          </Link>
                          <button
                            type="button"
                            onClick={() => toggleFavorite({ type: item.type, id: item.id, title: item.title })}
                            aria-label={`移除收藏 ${item.title}`}
                            className="w-6 h-6 flex items-center justify-center rounded text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer shrink-0"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="mt-2 pt-2 border-t border-background-200 text-[11px] text-foreground-400 px-1">
                    收藏保存在本设备浏览器中，可跨页面同步。
                  </p>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
              aria-expanded={mobileOpen}
              className="lg:hidden w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-lg`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-fade">
          <form onSubmit={submitSearch} className="flex items-center gap-2 mb-3" role="search">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索资讯、作品、角色"
                aria-label="移动端站内搜索"
                className="w-full h-10 rounded-full border border-background-300 bg-background-100 pl-8 pr-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="h-10 px-4 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
          <nav aria-label="移动端主导航" className="flex flex-col">
            {navItems.map((item) => {
              const active = item.match(location.pathname);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center justify-between px-2 py-3 text-sm border-b border-background-200 last:border-b-0 cursor-pointer ${
                    active ? "text-primary-600 font-medium" : "text-foreground-700"
                  }`}
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}